import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import { pageHead, SITE_URL } from "@/lib/seo";
import { getPost, type PostCard, type PostFull } from "@/lib/blog.functions";

const WHATSAPP =
  "https://wa.me/5545999175813?text=" +
  encodeURIComponent("Olá! Li um artigo no site e gostaria de agendar uma avaliação com o Dr. Kelson Kawamura.");

export const Route = createFileRoute("/$slug")({
  loader: async ({ params }) => {
    const data = await getPost({ data: { slug: params.slug } });
    if (!data.post) throw notFound();
    return data;
  },
  head: ({ loaderData, params }) => {
    const post = (loaderData as { post: PostFull | null } | undefined)?.post;
    if (!post) return {};
    return pageHead({
      title: post.seo_title || post.h1_title,
      description: post.meta_description || post.excerpt || "",
      path: `/${params.slug}`,
      image: post.cover_image_url || "/og-image.jpg",
      type: "article",

    });
  },
  component: PostPage,
  errorComponent: () => (
    <SiteShell>
      <section className="kk-section">
        <div className="kk-container">
          <h1 className="kk-h2">Conteúdo indisponível</h1>
          <p className="kk-lead">Não foi possível carregar este artigo agora.</p>
          <Link to="/blog" className="kk-button-primary">Ver todos os artigos</Link>
        </div>
      </section>
    </SiteShell>
  ),
});

function fmt(date: string | null) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
}

function PostPage() {
  const { post, related } = Route.useLoaderData() as { post: PostFull; related: PostCard[] };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.seo_title || post.h1_title,
    description: post.meta_description || post.excerpt || "",
    image: post.cover_image_url ? [post.cover_image_url] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/${post.slug}` },
    author: {
      "@type": "Physician",
      name: "Dr. Kelson Kawamura",
      medicalSpecialty: "PlasticSurgery",
      url: `${SITE_URL}/dr-kelson-kawamura`,
    },
    publisher: {
      "@type": "MedicalBusiness",
      name: "Kawamura Cirurgia Plástica",
      url: SITE_URL,
    },
  };

  return (
    <SiteShell>
      <div className="kk-page-top" />
      <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: post.h1_title }]} />

      <article className="kk-section">
        <div className="kk-container kk-post">
          <header className="kk-post__header">
            {post.category ? <span className="kk-eyebrow">{post.category.name}</span> : null}
            <h1 className="kk-h2">{post.h1_title}</h1>
            <p className="kk-post__meta">
              Dr. Kelson Kawamura · CRM 32174 · RQE 24787 {post.published_at ? `· ${fmt(post.published_at)}` : ""}
            </p>
          </header>

          {post.cover_image_url ? (
            <figure className="kk-post__cover">
              <img src={post.cover_image_url} alt={post.cover_image_alt || post.h1_title} />
            </figure>
          ) : null}

          <div className="kk-prose" dangerouslySetInnerHTML={{ __html: post.content_html }} />

          <div className="kk-post__cta">
            <p>
              Quer saber se este procedimento é indicado para você? A <strong>avaliação individualizada</strong> é o
              primeiro passo para um <strong>plano cirúrgico seguro</strong>.
            </p>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="kk-button-primary">
              Agendar avaliação <WhatsAppIcon />
            </a>
          </div>
        </div>
      </article>

      {related.length ? (
        <section className="kk-section kk-section--alt">
          <div className="kk-container">
            <h2 className="kk-h3">Leia também</h2>
            <div className="kk-post-grid">
              {related.map((item) => (
                <Link key={item.slug} to="/$slug" params={{ slug: item.slug }} className="kk-post-card">
                  <div className="kk-post-card__media">
                    {item.cover_image_url ? (
                      <img src={item.cover_image_url} alt={item.cover_image_alt || item.h1_title} loading="lazy" />
                    ) : (
                      <div className="kk-post-card__placeholder" aria-hidden="true" />
                    )}
                  </div>
                  <div className="kk-post-card__body">
                    <h3>{item.h1_title}</h3>
                    {item.excerpt ? <p>{item.excerpt}</p> : null}
                  </div>
                </Link>
              ))}
            </div>
            <div className="kk-post__more">
              <Link to="/blog" className="kk-button-secondary">
                Ver todos os artigos
              </Link>
            </div>
          </div>
        </section>
      ) : null}

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </SiteShell>
  );
}
